<template>
  <section class="form-fw__content">
    <form
      v-if="status !== isFailed"
      class="form--custom"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <fieldset class="form__container" :disabled="status === isLoading">
        <header class="form__container__header">
          <h2 class="form__container__title">{{ $t(`${namespace}.containerUsagePolicyTitle`) }}</h2>
          <p class="form__container__desc">{{ $t(`${namespace}.containerUsagePolicyDesc`) }}</p>
        </header>

        <div class="form__container__content pb-0">
          <label>{{ $t(`${namespace}.fieldPolicyLabel`) }}</label>
          <v-text-field
            v-model="form.name"
            :placeholder="$t(`${namespace}.fieldPolicyPlaceholder`)"
            :error-messages="errors.collect($t(`${namespace}.fieldPolicyName`))"
            v-validate="{ required: true }"
            :data-vv-name="$t(`${namespace}.fieldPolicyName`)"
            required
            flat
            outline
            solo
            color="accent"
          />

          <label>{{ $t(`${namespace}.fieldDescriptionLabel`) }}</label>
          <v-text-field
            v-model="form.description"
            :placeholder="$t(`${namespace}.fieldDescriptionPlaceholder`)"
            :error-messages="errors.collect($t(`${namespace}.fieldDescriptionName`))"
            v-validate="{ required: true }"
            :data-vv-name="$t(`${namespace}.fieldDescriptionName`)"
            required
            flat
            outline
            solo
            color="accent"
          />

          <div v-if="status === isSuccess">
            <label>{{ $t(`${namespace}.fieldCostCenterLabel`) }}</label>
            <AutoComplete
              kind="costCenter"
              :data="costCenters"
              :placeholder="$t(`${namespace}.fieldCostCenterPlaceholder`)"
              :disabled="!globalCompany"
              multiple
              :companyId="globalCompany ? globalCompany.id : null"
              :isValid="isCostCenterValid"
              :validateMessage="$t(`${namespace}.fieldCostCenterError`)"
              required
              @input="setCostCenter"
            />
          </div>
        </div>
      </fieldset>

      <fieldset class="form__container" :disabled="status === isLoading">
        <header class="form__container__header">
          <h2 class="form__container__title">{{ $t(`${namespace}.containerUsageFrequencyTitle`) }}</h2>
          <p class="form__container__desc">{{ $t(`${namespace}.containerUsageFrequencyDesc`) }}</p>
        </header>

        <div class="form__container__content">
          <v-radio-group
            v-model="form.timePolicyType"
            class="pa-0 ma-0"
            v-validate="{ required: true }"
            :error-messages="errors.collect($t(`${namespace}.fieldTypeName`))"
            :data-vv-name="$t(`${namespace}.fieldTypeName`)"
            required
          >
            <v-radio value="workingDays">
              <template slot="label">
                <span v-html="$t(`${namespace}.workingDays`)"></span>
              </template>
            </v-radio>
            <v-radio value="customDays">
              <template slot="label">
                <span v-html="$t(`${namespace}.customDays`)"></span>
              </template>
            </v-radio>
          </v-radio-group>

          <TimeRangeComboBox
            v-if="form.timePolicyType"
            :timePolicyType="form.timePolicyType"
            :intervals="form.intervals"
            :submit="submit"
            :kind="kind"
            @updateIntervals="updateIntervals"
            @removeTimeRange="removeTimeRange"
          />
        </div>
      </fieldset>
    </form>

    <div
      v-if="status === isFailed"
      class="text-xs-center mt-5"
    >
      <span>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          href="#"
          :title="$t('Common.tryAgain')"
          class="text--link accent--text"
          @click.prevent="$emit('tryAgain')"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </span>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isObject, isArray } from 'utils/functions'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import AutoComplete from 'components/AutoComplete'
import TimeRangeComboBox from 'components/usagePolicy/timeRangeComboBox'

export default {
  name: 'Form',
  props: {
    kind: {
      type: String
    },
    data: {
      type: Object
    },
    submit: {
      type: Boolean
    },
    status: {
      type: Number
    }
  },
  components: {
    AutoComplete,
    TimeRangeComboBox
  },
  computed: {
    ...mapState({
      globalCompany: ({ company }) => company.selectedCompany,
      isAdmin: ({ session }) => session.user.business.isAdministrator
    }),

    policyId () {
      const routeParams = this.$route.params
      return routeParams.id ? Number(routeParams.id) : null
    },

    isLoading: () => STATUS_IS_LOADING,
    isSuccess: () => STATUS_SUCCESS,
    isFailed: () => STATUS_FAILED
  },
  data () {
    return {
      namespace: 'Views.UsagePolicyPage',
      isFetching: false,
      isCostCenterValid: true,
      costCenters: [],
      firstLoad: true,

      // form fields
      form: {
        timePolicyType: null,
        name: null,
        description: null,
        intervals: [],
        costCenters: []
      }
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error',
      infoAlert: 'snackbarAlert/info'
    }),

    isValidObject (val) {
      return isObject(val)
    },

    isValidArray (val) {
      return isArray(val)
    },

    setCostCenter (val) {
      if (this.isValidArray(val)) {
        this.costCenters = val
        this.form.costCenters = val.map(item => item.id)
        this.isCostCenterValid = true
        return
      }

      this.costCenters = []
      this.form.costCenters = []
      this.isCostCenterValid = false
    },

    setData (val) {
      if (this.isValidObject(val)) {
        const { timePolicyType, timePolicy, costCenter } = val
        const { name, description, intervals } = timePolicy

        this.form = {
          timePolicyType,
          name,
          description,
          intervals
        }

        this.setCostCenter(costCenter)
      }
    },

    updateIntervals (intervals) {
      this.form.intervals = intervals
    },

    removeTimeRange ({ startTime, endTime }) {
      this.form.intervals = this.form.intervals.filter(interval => {
        return interval.startTime !== startTime && interval.endTime !== endTime
      })
    },

    resetIntervals () {
      if (!this.firstLoad) {
        this.form.intervals = []
      }

      this.firstLoad = false
    },

    resetFields () {
      this.isFetching = false
      this.isCostCenterValid = true
      this.costCenters = []
      this.form = {
        timePolicyType: null,
        name: null,
        description: null,
        intervals: [],
        costCenters: []
      }
    },

    async createTimePolicy (data) {
      try {
        const companyId = this.globalCompany ? this.globalCompany.id : null
        const { name, description, intervals } = this.form

        const { data: res } = await this.$apiService.createTimePolicy({
          companyId,
          name,
          description,
          intervals
        })

        const policyId = res && res.data && res.data.id ? res.data.id : null
        return { status: 'success', policyId }
      } catch (err) {
        console.log('CREATE TIME POLICY ERROR:', err)
        return { status: 'error' }
      }
    },

    async updateTimePolicy () {
      try {
        const policyId = this.policyId
        const companyId = this.globalCompany ? this.globalCompany.id : null
        const { name, description, intervals } = this.form

        await this.$apiService.updateTimePolicy(policyId, {
          companyId,
          name,
          description,
          intervals
        })

        return { status: 'success' }
      } catch (err) {
        console.log('UPDATE TIME POLICY ERROR:', err)
        return { status: 'error' }
      }
    },

    async setCostCenterTimePolicy (policyId) {
      try {
        const { costCenters } = this.form
        await this.$apiService.setCostCenterTimePolicy(policyId, costCenters)
        return { status: 'success' }
      } catch (err) {
        console.log('SET COST CENTER ERROR:', err)
        return { status: 'error' }
      }
    },

    async create () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        const createTimePolicy = await this.createTimePolicy()
        let setCostCenterTimePolicy = { status: null }

        if (createTimePolicy.status === 'success' && createTimePolicy.policyId) {
          setCostCenterTimePolicy = await this.setCostCenterTimePolicy(createTimePolicy.policyId)
        }

        if (createTimePolicy.status === 'error' || setCostCenterTimePolicy.status === 'error') {
          if (createTimePolicy.policyId) {
            await this.$apiService.deleteTimePolicy(createTimePolicy.policyId)
          }

          throw new Error()
        }

        this.successAlert(this.$t(`${this.namespace}.usagePolicyCreateSuccessMessage`))
        this.$emit('setCurrentStatus', this.isSuccess)
        this.resetFields()
        scrollTo(0, 0)
        this.$router.push({ name: 'UsagePolicies' })
      } catch (err) {
        console.log('ERROR:', err)
        this.$emit('setCurrentStatus', this.isSuccess)
        this.errorAlert(this.$t(`${this.namespace}.usagePolicyCreateErrorMessage`))
      }
    },

    async edit () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        const [updateTimePolicy, setCostCenterTimePolicy] = await Promise.all([
          this.updateTimePolicy(),
          this.setCostCenterTimePolicy(this.policyId)
        ])

        if (updateTimePolicy.status === 'error' || setCostCenterTimePolicy.status === 'error') {
          throw new Error()
        }

        this.successAlert(this.$t(`${this.namespace}.usagePolicyEditSuccessMessage`))
        this.$emit('tryAgain')
        scrollTo(0, 0)
        this.$router.push({ name: 'UsagePolicies' })
      } catch (err) {
        console.log('ERROR:', err)
        this.$emit('setCurrentStatus', this.isSuccess)
        this.errorAlert(this.$t(`${this.namespace}.usagePolicyUpdateErrorMessage`))
      }
    },

    async handleSubmit () {
      try {
        const isValidForm = await this.$validator.validateAll()
        const isValidCostCenters = this.isValidArray(this.costCenters)
        const isValidIntervals = this.isValidArray(this.form.intervals)

        // autocomplete for validation
        if (!isValidCostCenters) this.isCostCenterValid = false

        // if (isValidForm && isValidCostCenters && !isValidIntervals) {
        if (!isValidIntervals) {
          this.infoAlert(this.$t(`${this.namespace}.emptyPeriodMessage`))
        }

        if (isValidForm && isValidCostCenters && isValidIntervals) {
          this.kind === 'editar'
            ? this.edit()
            : this.create()
        } else {
          const target = this.$validator.errors.items[0]
          if (target) {
            const elem = this.$el.querySelector(`[data-vv-name="${target.field}"]`)
            if (elem) {
              elem.scrollIntoView(false)
              elem.focus()
            }
          }
        }
      } catch (err) {
        console.log('ERROR:', err)
      }
    }
  },
  watch: {
    submit () {
      this.handleSubmit()
    },

    data (val) {
      this.setData(val)
    },

    'form.timePolicyType' (val) {
      this.resetIntervals()
    }
  },
  mounted () {
    this.isFetching = true

    if (this.kind !== 'editar' && this.globalCompany) {
      setTimeout(() => { this.isFetching = false }, 200)
      return
    }

    this.setData(this.data)
  }
}
</script>
